import { Injectable } from '@angular/core';
import { PartnerService } from './partner.service';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { Partner } from '../models/partner';
import { ConnectionService } from '../../../services/connection/connection.service';
import { LoggerService } from '../../../services/logger/logger.service';
import { RemovePartnerResult } from '../models/remove-partner-result';

@Injectable()
export class PartnerOdooService extends PartnerService {

  constructor(
    private connectionService: ConnectionService,
    private logger: LoggerService,
  ) {
    super();
  }

  createPartner(partner: Partner, password: string, sendEmail: boolean =  true): Observable<Partner> {
    return of(true).pipe(
      switchMap(_ => {
        if (!partner) {
          this.logger.error(`PartnerOdooService: createPartner parameter partner is undefined.`);
          throw new Error('Parameter partner is undefined.');
        }
        const partnerData = partner.toJSON();
        partnerData.password = password;
        partnerData.send_email = sendEmail;
        this.logger.info(`PartnerOdooService: createPartner requested`);
        return this.connectionService.sendJsonPostRequest('/store/partner/create', partnerData);
      }),
      map(createdPartner => Partner.createFromAny(createdPartner)),
      tap(createdPartner => {
        if (createdPartner) {
          this.logger.info(`PartnerOdooService: createPartner. Result partner '${createdPartner.id}', '${createdPartner.firstName} ${createdPartner.lastName}' received.`);
        } else {
          this.logger.error(`PartnerOdooService: createPartner. Error to get created partner, it is undefined.`);
        }
      }),
    );
  }

  registerPartnerAuthorization(partnerId: number, email: string, password: string): Observable<Partner> {
    return of(true).pipe(
      switchMap(_ => {
        this.logger.info(`PartnerOdooService: createPartner requested`);
        return this.connectionService.sendJsonPostRequest('/store/partner/register-partner-authorization', {
          partner_id: partnerId,
          email,
          password,
        });
      }),
      map(partner => Partner.createFromAny(partner)),
      tap(partner => {
        if (partner) {
          this.logger.info(`PartnerOdooService: registerPartnerAuthorization. Result partner '${partner.id}',
           '${partner.email}' received.`);
        } else {
          this.logger.error(`PartnerOdooService: registerPartnerAuthorization. Error to get registered partner, it is undefined.`);
        }
      }),
    );
  }

  get loggedInPartner(): Observable<Partner> {
    return of(true).pipe(
      switchMap(_ => {
        if (!this.connectionService.isAuthorizationTokenExist) {
          this.logger.error(`PartnerOdooService: loggedInPartner authorization token is not exist.`);
          return of(null);
        }

        this.logger.info(`PartnerOdooService: loggedInPartner requested.`);
        return this.connectionService.sendJsonPostRequest('/store/partner/logged-in').pipe(
          map(partner => Partner.createFromAny(partner)),
          tap(partner => {
            if (partner) {
              this.logger.info(`PartnerOdooService: loggedInPartner partner '${partner.id}', '${partner.firstName} ${partner.lastName}' received.`);
            } else {
              this.logger.error(`PartnerOdooService: loggedInPartner. Error to get partner, it is undefined.`);
            }
          }),
          catchError(error => {
            return of(null);
          }),
        );
      }),
    );
  }

  updatePartner(partner: Partner): Observable<Partner> {
    return of(true).pipe(
      switchMap(_ => {
        if (!partner) {
          this.logger.error(`PartnerOdooService: updatePartner parameter partner is undefined.`);
          throw new Error('Parameter partner is undefined.');
        }

        this.logger.info(`PartnerOdooService: updatePartner requested`);
        return this.connectionService.sendJsonPostRequest('/store/partner/update', partner.toJSON());
      }),
      map(partnerData => Partner.createFromAny(partnerData)),
      tap(partnerData => {
        if (partnerData) {
          this.logger.info(`PartnerOdooService: updatePartner. Partner: '${partnerData.id}', '${partnerData.firstName} ${partnerData.lastName}' received.`);
        } else {
          this.logger.info(`PartnerOdooService: updatePartner. Error.`);
        }
      }),
    );
  }

  getChildren(): Observable<Partner[]> {
    return of([]);
  }

  createChild(child: Partner): Observable<Partner> {
    return of(true).pipe(
      switchMap(_ => {
        if (!child) {
          this.logger.error(`PartnerOdooService: createChild parameter child is undefined.`);
          throw new Error('Parameter partner is undefined.');
        }

        this.logger.info(`PartnerOdooService: createChild requested`);
        return this.connectionService.sendJsonPostRequest('/store/partner/create-child', child.toJSON());
      }),
      map(partner => Partner.createFromAny(partner)),
      tap(partner => {
        if (partner) {
          this.logger.info(`PartnerOdooService: createChild. child: '${partner.id}', '${partner.firstName} ${partner.lastName}' received.`);
        } else {
          this.logger.error(`PartnerOdooService: createChild. Error to get created child, it is undefined.`);
        }
      }),
    );
  }

  updateChild(child: Partner): Observable<Partner> {
    return of(true).pipe(
      switchMap(_ => {
        if (!child) {
          this.logger.error(`PartnerOdooService: updateChild parameter child is undefined.`);
          throw new Error('Parameter partner is undefined.');
        }

        this.logger.info(`PartnerOdooService: updateChild requested`);
        return this.connectionService.sendJsonPostRequest('/store/partner/update-child', child.toJSON());
      }),
      map(partner => Partner.createFromAny(partner)),
      tap(partner => {
        if (partner) {
          this.logger.info(`PartnerOdooService: updateChild. child: '${partner.id}', '${partner.firstName} ${partner.lastName}' received.`);
        } else {
          this.logger.error(`PartnerOdooService: updateChild. Error to get updated child, it is undefined.`);
        }
      }),
    );
  }

  removeChild(child: Partner): Observable<boolean> {
    return of(true).pipe(
      switchMap(_ => {
        if (!child) {
          this.logger.error(`PartnerOdooService: removeChild parameter child is undefined.`);
          throw new Error('Parameter partner is undefined.');
        }

        this.logger.info(`PartnerOdooService: removeChild requested`);
        return this.connectionService.sendJsonPostRequest('/store/partner/remove-child', { childId: child.id });
      }),
      map(result => !!result),
      tap(result => {
        if (result) {
          this.logger.info(`PartnerOdooService: removeChild. result: '${result}' received.`);
        } else {
          this.logger.error(`PartnerOdooService: removeChild. Error.`);
        }
      }),
    );
  }

  removeLoggedInPartner(password: string): Observable<RemovePartnerResult> {
    return this.connectionService.sendJsonPostRequest('/store/partner/remove', { password })
      .pipe(
        map(result => RemovePartnerResult.createFromAny(result))
      );
  }

  uploadPhotoToField(photo: string, fieldToUpload: string): Observable<boolean> {
    return of(true).pipe(
      switchMap(_ => {
        if (!photo || !fieldToUpload) {
          this.logger.error(`PartnerOdooService: uploadPhotos parameter partner is undefined.`);
          throw new Error('Parameter partner is undefined.');
        }

        this.logger.info(`PartnerOdooService: updatePartner requested`);
        return this.connectionService.sendJsonPostRequest('/store/partner/upload-photo-to-field', {photo, fieldToUpload});
      }),
      tap(result => {
        if (result) {
          this.logger.info(`PartnerOdooService: uploadPhotos. Success.`);
        } else {
          this.logger.info(`PartnerOdooService: uploadPhotos. Error.`);
        }
      }),
    );
  }
}
